import React, { useEffect } from "react";
import { Input, Button, Card, Row, Col } from "antd";
import { CalculatorOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Typography } from "@mui/material";
import { Button as MUIButton } from "@mui/material";
import BadgeButton from "components/AdminBooks/BadgeButton";

export const NATIVE_NON_NATIVE_SEED_ROWS = [
  {
    title: "NonNative",
    description:
      "Coloca a los estudiantes que aprenden español como segunda lengua o con exposición limitada. La puntuación en esta banda refleja el progreso típico de hablantes no nativos.",
    description_english:
      "Places students learning Spanish as a second language or with limited exposure. Scores in this band reflect typical non-native speaker progress.",
  },
  {
    title: "Native",
    description:
      "Coloca a los estudiantes con competencia cercana a la de un hablante nativo en contextos académicos. La puntuación en esta banda indica dominio avanzado.",
    description_english:
      "Places students with proficiency closer to a native speaker in academic contexts. Scores in this band indicate advanced mastery.",
  },
];

export const splitNativeNonNativePointsAcrossLevels = (maxPoint, levelCount, existingLevels = []) => {
  const count = Math.max(1, levelCount);
  const maxVal = Math.max(0, Math.floor(Number(maxPoint) || 0));
  const total = maxVal + 1;
  const baseSize = Math.floor(total / count);
  const remainder = total % count;
  const newLevels = [];
  let current = 0;
  for (let i = 0; i < count; i += 1) {
    const segmentSize = baseSize + (i < remainder ? 1 : 0);
    const min = current;
    const max = current + segmentSize - 1;
    current = max + 1;
    const existing = existingLevels[i];
    newLevels.push({
      title: existing?.title ?? "",
      min,
      max,
      description: existing?.description ?? "",
      description_english: existing?.description_english ?? "",
    });
  }
  return newLevels;
};

export const buildDefaultNativeNonNativeLevels = (maxPoint) =>
  splitNativeNonNativePointsAcrossLevels(maxPoint, NATIVE_NON_NATIVE_SEED_ROWS.length, NATIVE_NON_NATIVE_SEED_ROWS);

export const validateNativeNonNativeLevels = (levels, maxValue) => {
  if (!levels || levels.length === 0) {
    return "Add at least one Native / NonNative band.";
  }
  const maxV = Math.floor(Number(maxValue) || 0);
  if (maxV < 0) {
    return "Invalid max points.";
  }

  const parsed = [];
  for (let i = 0; i < levels.length; i += 1) {
    const row = levels[i];
    const title = String(row?.title ?? "").trim();
    if (!title) {
      return `Band ${i + 1}: title is required.`;
    }
    const minRaw = row?.min;
    const maxRaw = row?.max;
    if (minRaw === "" || minRaw === null || minRaw === undefined) {
      return `Band ${i + 1}: min is required.`;
    }
    if (maxRaw === "" || maxRaw === null || maxRaw === undefined) {
      return `Band ${i + 1}: max is required.`;
    }
    const min = Number(minRaw);
    const max = Number(maxRaw);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return `Band ${i + 1}: min and max must be valid numbers.`;
    }
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
      return `Band ${i + 1}: min and max must be whole numbers.`;
    }
    if (min > max) {
      return `Band ${i + 1}: min cannot be greater than max.`;
    }
    if (min < 0 || max > maxV) {
      return `Band ${i + 1}: range must stay between 0 and ${maxV}.`;
    }
    parsed.push({ min, max, order: i });
  }

  parsed.sort((a, b) => a.min - b.min || a.order - b.order);

  let expected = 0;
  for (let j = 0; j < parsed.length; j += 1) {
    const { min, max, order } = parsed[j];
    if (min < expected) {
      return `Native / NonNative ranges overlap or are out of order (check band ${order + 1}).`;
    }
    if (min > expected) {
      return `Points ${expected} to ${min - 1} are not covered by any band. Cover every point from 0 to ${maxV} with no gaps.`;
    }
    expected = max + 1;
  }
  if (expected !== maxV + 1) {
    return `Native / NonNative ranges must cover every point from 0 through ${maxV}. Points ${expected} to ${maxV} are missing.`;
  }
  return null;
};

export const getNativeNonNativeConfigRows = (orgConfig, examCode) => {
  if (!orgConfig || !examCode) return [];
  const nested = orgConfig.config?.native_non_native_config?.[examCode];
  if (Array.isArray(nested) && nested.length > 0) return nested;
  const legacy = orgConfig.native_non_native_config?.[examCode];
  if (Array.isArray(legacy) && legacy.length > 0) return legacy;
  return [];
};

export const isNativeNonNativeBandConfigActive = (rows, maxPoints) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const maxV = Math.floor(Number(maxPoints) || 0);
  if (maxV < 0) return false;
  return validateNativeNonNativeLevels(rows, maxV) === null;
};

export const getNativeNonNativeLabelFromPoints = (grade, maxPoints, rows) => {
  const numGrade = Number(grade);
  if (Number.isNaN(numGrade)) return "-";
  const maxV = Number(maxPoints);
  if (!Number.isFinite(maxV) || maxV < 0) return "-";
  if (!isNativeNonNativeBandConfigActive(rows, maxV)) return "-";
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const min = Number(r.min);
    const max = Number(r.max);
    if (Number.isFinite(min) && Number.isFinite(max) && numGrade >= min && numGrade <= max) {
      const t = String(r.title ?? "").trim();
      if (t) return t;
    }
  }
  return "-";
};

const defaultRow = () => ({
  title: "",
  min: "",
  max: "",
  description: "",
  description_english: "",
});

const NativeNonNativeConfiguration = ({ levels = [], onChange, disabled = false, max_value }) => {
  useEffect(() => {
    if (!disabled && levels.length === 0 && (max_value || max_value === 0)) {
      onChange(buildDefaultNativeNonNativeLevels(max_value));
    }
  }, [levels, disabled, onChange, max_value]);

  const handleChange = (index, field, value) => {
    const updated = [...levels];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAddLevel = (index = null) => {
    const updated = [...levels];
    if (index !== null) {
      updated.splice(index + 1, 0, defaultRow());
    } else {
      updated.push(defaultRow());
    }
    onChange(updated);
  };

  const handleDeleteLevel = (index) => {
    const updated = [...levels];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleCalculateRange = () => {
    const levelCount = Math.max(1, levels.length);
    const next = splitNativeNonNativePointsAcrossLevels(max_value, levelCount, levels);
    onChange(next);
  };

  return (
    <div style={{ width: "100%", marginTop: 24 }}>
      <div className="flex items-center justify-between mb-10px">
        <Typography variant="h6" gutterBottom>
          Native / NonNative configuration
        </Typography>
        <div className="flex items-center gap-10px">
          <div style={{ whiteSpace: "nowrap" }}>
            Max Points: <b>{max_value}</b>
          </div>
          <BadgeButton
            style={{ width: "fit-content" }}
            variant="contained"
            color="primary"
            startIcon={<CalculatorOutlined />}
            onClick={handleCalculateRange}
            disabled={disabled || (!max_value && max_value !== 0)}
          >
            Calculate Range
          </BadgeButton>
        </div>
      </div>
      {levels.map((level, index) => (
        <Card
          key={index}
          style={{ marginBottom: 16 }}
          title={level.title ? level.title : `Band ${index + 1}`}
          extra={
            !disabled && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteLevel(index)}
              />
            )
          }
        >
          <Row gutter={12}>
            <Col span={12}>
              <Input
                placeholder="Title (e.g. NonNative, Native)"
                value={level.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Min"
                value={level.min}
                onChange={(e) => handleChange(index, "min", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Max"
                value={level.max}
                onChange={(e) => handleChange(index, "max", e.target.value)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Typography variant="subtitle2" gutterBottom>
                Description
              </Typography>
              <Input.TextArea
                rows={3}
                placeholder="Description"
                value={level.description}
                onChange={(e) => handleChange(index, "description", e.target.value)}
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 12 }}>
            <Col span={24}>
              <Typography variant="subtitle2" gutterBottom>
                Description (English)
              </Typography>
              <Input.TextArea
                rows={3}
                placeholder="Description (English)"
                value={level.description_english}
                onChange={(e) => handleChange(index, "description_english", e.target.value)}
                disabled={disabled}
              />
            </Col>
          </Row>
          {!disabled && (
            <Row justify="end" style={{ marginTop: 10 }}>
              <Button type="dashed" onClick={() => handleAddLevel(index)}>
                Add Below
              </Button>
            </Row>
          )}
        </Card>
      ))}
      {!disabled && (
        <Row justify="start" style={{ marginTop: 8 }}>
          <MUIButton
            variant="contained"
            color="primary"
            startIcon={<PlusOutlined />}
            onClick={() => handleAddLevel(null)}
          >
            Add More
          </MUIButton>
        </Row>
      )}
    </div>
  );
};

export default NativeNonNativeConfiguration;
